import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Network, Eye, EyeOff, CheckCircle, XCircle, Trophy, RotateCcw, AlertTriangle, ArrowRight } from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';
import { modernNetworkingBank } from '../../../data/ModernNetworkingBank';
import { withKinematicSensors } from '../../../core/hocs/withKinematicSensors';

function SpatialAuditGameBase({ gameData, onComplete, onBack, resetKinematics, getKinematicMetrics, startHover, endHover }) {
  const { playSuccess, playError, playWin, playExtinguish } = useGameSounds();

  const { id = 'game_modern_networking', preGameBriefing, failureLogic } = gameData || {};

  const deckRef = useRef([]);

  if (deckRef.current.length === 0) {
    let array = [...modernNetworkingBank];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    deckRef.current = array.slice(0, 5);
  }

  const activeDeck = deckRef.current;

  const [stage, setStage] = useState('briefing');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredNodeIndex, setHoveredNodeIndex] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [sessionAttempts, setSessionAttempts] = useState([]);

  const activeScenario = activeDeck[currentIndex];

  const shuffledOptions = useMemo(() => {
    if (!activeScenario) return [];
    return [...activeScenario.options].sort(() => Math.random() - 0.5);
  }, [activeScenario]);

  useEffect(() => {
    if (preGameBriefing && currentIndex === 0) {
      setStage('briefing');
    } else {
      setStage('playing');
      resetKinematics(); // Clear out pre-game hovering
    }
  }, [preGameBriefing, currentIndex, resetKinematics]);

  const handleNodeMouseEnter = (nodeIndex, nodeTitle) => {
    setHoveredNodeIndex(nodeIndex);
    startHover(nodeTitle);
  };

  const handleNodeMouseLeave = (nodeIndex, nodeTitle) => {
    endHover(nodeTitle);
    setHoveredNodeIndex(null);
  };

  const handleSelectNode = (option) => {
    if (hoveredNodeIndex !== null) {
      endHover(option.nodeTitle);
    }

    const metrics = getKinematicMetrics();
    const isOptimal = option.category === 'Optimal';

    const interactionRecord = {
      scenarioId: activeScenario.id,
      selectedNode: option.nodeTitle,
      category: option.category,
      isCorrect: isOptimal,
      ttfiMs: metrics.ttfi,
      hoverDurationsMs: metrics.hoverDurationsMs,
      timestamp: Date.now()
    };

    setSessionAttempts(prev => [...prev, interactionRecord]);

    resetKinematics(); // Reset for the next scenario

    if (isOptimal) {
      playSuccess();
      setScore(prev => prev + 1);
      setFeedback({ type: 'success', text: option.feedback });
      nextScenario(1200);
    } else {
      playError();
      setFeedback({ type: 'error', text: option.feedback });

      if (option.category === 'Irrelevant' || option.category === 'Decision Drift Trap') {
        setTimeout(() => triggerIntervention(option.category), 2500);
      } else {
        nextScenario(2500);
      }
    }
  };

  const nextScenario = (delay = 1200) => {
    setTimeout(() => {
      setFeedback(null);
      if (currentIndex + 1 < activeDeck.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        playWin();
        setStage('results');
      }
    }, delay);
  };

  const triggerIntervention = (category) => {
    playExtinguish();
    const logic = failureLogic?.[category] || {
      title: "Navigation Breakdown",
      script: "You engaged a non-optimal organizational node. In enterprise environments, escalating across incorrect authority boundaries creates decision drift and operational friction.",
      videoUrl: null
    };
    setActiveIntervention(logic);
    setStage('intervention');
  };

  const handleRetry = () => {
    let array = [...modernNetworkingBank];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    deckRef.current = array.slice(0, 5);

    setCurrentIndex(0);
    setScore(0);
    setFeedback(null);
    setActiveIntervention(null);
    setStage('playing');
    resetKinematics();
  };

  const handleFinalSubmit = () => {
    const percentage = Math.round((score / activeDeck.length) * 100);
    const passed = percentage >= 80;

    const payload = {
      id,
      rawInteractions: [
        ...sessionAttempts,
        {
          status: passed ? 'success' : 'failed',
          score: percentage,
          timestamp: Date.now()
        }
      ]
    };

    if (typeof onComplete === 'function') {
      onComplete(payload);
    }
  };

  if (!activeScenario && stage !== 'results') return null;

  return (
    <div className="h-full flex flex-col bg-slate-950 text-slate-200 rounded-xl overflow-hidden border border-slate-800 relative min-h-[550px] font-sans">
      
      <AnimatePresence>
        {stage === 'briefing' && preGameBriefing && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay
              type="briefing"
              data={preGameBriefing}
              onContinue={() => setStage('playing')}
            />
          </div>
        )}
        {stage === 'intervention' && activeIntervention && (
          <div className="absolute inset-0 z-[100]">
            <TacticalOverlay
              type="intervention"
              data={activeIntervention}
              onRetry={handleRetry}
            />
          </div>
        )}
      </AnimatePresence>

      {stage === 'results' && (
        <div className="p-8 flex flex-col items-center justify-center text-center h-full animate-fade-in absolute inset-0 bg-slate-900 z-50">
          <div className={`mb-6 p-6 rounded-full ${score >= 4 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
            {score >= 4 ? <Trophy size={48} /> : <XCircle size={48} />}
          </div>

          <h2 className="text-3xl font-black text-white uppercase tracking-wider mb-2">
            {score >= 4 ? "Spatial Audit Complete" : "Navigation Failed"}
          </h2>

          <p className="text-slate-400 mb-8 max-w-sm">
            You accurately engaged <span className="font-bold text-white">{score}/{activeDeck.length}</span> optimal organizational directory nodes ({Math.round((score / activeDeck.length) * 100)}%).
          </p>

          <div className="flex gap-4">
            <button onClick={handleRetry} className="px-6 py-3 bg-slate-800 text-slate-300 font-bold rounded-xl hover:bg-slate-700 transition">
              Retry Audit
            </button>
            <button onClick={handleFinalSubmit} className="px-8 py-3 bg-emerald-600 text-white font-black uppercase tracking-wider rounded-xl shadow-lg hover:bg-emerald-500 transition">
              Complete Module
            </button>
          </div>
        </div>
      )}

      {stage === 'playing' && (
        <div className="flex flex-col h-full p-6">
          
          <div className="flex justify-between items-center pb-4 border-b border-slate-800 mb-6">
            <div className="flex items-center gap-2">
              <Network size={20} className="text-indigo-400" />
              <span className="text-xs font-black uppercase tracking-widest text-slate-300">Spatial Directory Audit</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono font-bold text-indigo-400">Node {currentIndex + 1} / {activeDeck.length}</span>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 p-5 rounded-2xl mb-8 shadow-inner">
            <p className="text-xs font-black uppercase tracking-widest text-indigo-400 mb-2">Operational Context</p>
            <p className="text-slate-200 text-sm md:text-base leading-relaxed font-medium">
              {activeScenario.scenarioContext}
            </p>
          </div>

          <div className="flex-1 flex flex-col justify-center">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider text-center mb-4 flex items-center justify-center gap-2">
              <Eye size={14} className="text-indigo-400" /> Hover over directory nodes to inspect authority before engaging
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {shuffledOptions.map((option, idx) => {
                const isHovered = hoveredNodeIndex === idx;

                return (
                  <motion.button
                    key={idx}
                    onMouseEnter={() => handleNodeMouseEnter(idx, option.nodeTitle)}
                    onMouseLeave={() => handleNodeMouseLeave(idx, option.nodeTitle)}
                    onClick={() => handleSelectNode(option)}
                    disabled={!!feedback}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-5 rounded-2xl border text-left transition-all duration-300 group overflow-hidden ${
                      isHovered
                        ? 'bg-slate-900 border-indigo-500/80 shadow-[0_0_20px_rgba(99,102,241,0.2)]'
                        : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-500">
                        Directory Node 0{idx + 1}
                      </span>
                      {isHovered ? (
                        <Eye size={14} className="text-indigo-400 animate-pulse" />
                      ) : (
                        <EyeOff size={14} className="text-slate-600" />
                      )}
                    </div>

                    <div className="min-h-[48px] flex items-center">
                      <AnimatePresence mode="wait">
                        {isHovered ? (
                          <motion.p
                            key="revealed"
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            className="font-bold text-white text-sm md:text-base leading-snug"
                          >
                            {option.nodeTitle}
                          </motion.p>
                        ) : (
                          <motion.div
                            key="masked"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center gap-2 text-slate-600 font-mono text-xs select-none"
                          >
                            <div className="h-4 w-32 bg-slate-800 rounded animate-pulse" />
                            <span className="text-[10px] uppercase font-bold tracking-widest text-slate-700">[Hover to Inspect]</span>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          <AnimatePresence>
            {feedback && (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className={`absolute inset-6 z-50 rounded-2xl backdrop-blur-md border-4 p-8 flex flex-col items-center justify-center text-center shadow-2xl ${
                  feedback.type === 'success'
                    ? 'bg-slate-950/90 border-emerald-500'
                    : 'bg-slate-950/90 border-red-500'
                }`}
              >
                {feedback.type === 'success' ? (
                  <CheckCircle className="w-16 h-16 text-emerald-400 mb-4" />
                ) : (
                  <XCircle className="w-16 h-16 text-red-400 mb-4" />
                )}
                <h3 className={`text-2xl font-black uppercase mb-3 ${feedback.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {feedback.type === 'success' ? 'Optimal Route Engaged' : 'Navigation Error'}
                </h3>
                <p className="text-slate-200 text-sm md:text-base leading-relaxed max-w-md font-medium">
                  {feedback.text}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="pt-4 border-t border-slate-900 flex justify-between items-center mt-auto">
            <button onClick={onBack} className="text-xs font-bold text-slate-500 hover:text-slate-300 transition-colors">
              Exit Simulation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default withKinematicSensors(SpatialAuditGameBase);